import moment, { Moment, LongDateFormatKey } from 'moment';
import 'moment/locale/da';
import 'moment/locale/en-ie';
// Enums
import {
    DateTimeTemplate,
    DateTimeFormat,
    LanguageLocaleMapper,
    LanguageDateTimeFormatSeparatorOverride,
} from '../enums';

/**
 * Returns the standard memoActionDateFormat
 * @param date
 * @param isoCode
 * @returns
 */
export const memoActionDateFormat = (date: number | Date, isoCode: string): string => {
    return moment(date).format('D. MMM y');
};

/**
 * Returns the standard memoActionDateFormat
 * @param date
 * @param isoCode
 * @returns
 */
export const memoActionStartDateFormat = (date: number | Date, isoCode: string): string => {
    return moment(date).format('D. MMM y,  HH.mm');
};

export const documentHeaderFormat = (date: number | Date, language: string): string => {
    return moment(date).lang(language).format('D. MMMM, y');
};

export const getUtcDate = (): Date => {
    return new Date(moment.utc().format());
};

export const getUTCString = (value: string, template: DateTimeTemplate | string, language: string): string => {
    const isDateTimeValid = isValidDateAndTime(value, template, language);

    if (isDateTimeValid) {
        const locale = LanguageLocaleMapper[language];
        const momentObject = moment(value, template, locale);
        return momentObject.utc(true).format();
    } else {
        return '';
    }
};

export const isValidDateAndTime = (
    value: string | Moment,
    template: DateTimeTemplate | string,
    language: string
): boolean => {
    if (typeof value === 'string') {
        const locale = LanguageLocaleMapper[language];
        const momentObject = moment(value, template, locale);
        return value.indexOf(momentObject.format(template)) > -1;
    } else {
        return value && value.isValid();
    }
};

export const getDateTimeTemplate = (format: DateTimeFormat, language: string, overrideSeparator = true) => {
    if (!format) {
        return null;
    }

    const locale = LanguageLocaleMapper[language];
    switch (format) {
        case DateTimeFormat.DATE_AND_TIME:
            return (
                (overrideSeparator
                    ? replaceSeparator(moment.localeData(locale).longDateFormat(DateTimeTemplate.ONLY_DATE), language)
                    : moment.localeData(locale).longDateFormat(DateTimeTemplate.ONLY_DATE)) +
                ' ' +
                moment.localeData(locale).longDateFormat(DateTimeTemplate.ONLY_TIME)
            );

        case DateTimeFormat.FULL_DATE_AND_TIME:
            return (
                moment.localeData(locale).longDateFormat(DateTimeTemplate.FULL_DATE) +
                ' ' +
                moment.localeData(locale).longDateFormat(DateTimeTemplate.FULL_TIME)
            );

        case DateTimeFormat.FULL_DATE_AND_TIME_NUMERIC:
            return (
                (overrideSeparator
                    ? replaceSeparator(moment.localeData(locale).longDateFormat(DateTimeTemplate.ONLY_DATE), language)
                    : moment.localeData(locale).longDateFormat(DateTimeTemplate.ONLY_DATE)) +
                ' - ' +
                moment.localeData(locale).longDateFormat(DateTimeTemplate.FULL_TIME)
            );

        case DateTimeFormat.ONLY_DAY:
            return (
                DateTimeTemplate.SHORT_WEEKDAY +
                ' ' +
                moment.localeData(locale).longDateFormat(DateTimeTemplate.ONLY_DATE)
            );

        case DateTimeFormat.ONLY_HOUR:
            return (
                moment.localeData(locale).longDateFormat(DateTimeTemplate.ONLY_TIME) +
                ' ' +
                (overrideSeparator
                    ? replaceSeparator(moment.localeData(locale).longDateFormat(DateTimeTemplate.ONLY_DATE), language)
                    : moment.localeData(locale).longDateFormat(DateTimeTemplate.ONLY_DATE))
            );

        default:
            if (locale && isLongDateFormatKey(DateTimeTemplate[format])) {
                return overrideSeparator
                    ? replaceSeparator(
                          moment.localeData(locale).longDateFormat(DateTimeTemplate[format] as LongDateFormatKey),
                          language
                      )
                    : moment.localeData(locale).longDateFormat(DateTimeTemplate[format] as LongDateFormatKey);
            } else {
                return DateTimeTemplate[format];
            }
    }
};

const isLongDateFormatKey = (candidate: string): candidate is LongDateFormatKey => {
    return ['LTS', 'LT', 'L', 'LL', 'LLL', 'LLLL'].includes(candidate.toUpperCase());
};

export const getLocalFromUTC = (value: string, template: DateTimeTemplate | string, language: string) => {
    const locale = LanguageLocaleMapper[language];
    const momentObject = moment.utc(value).locale(locale);
    return value ? momentObject.local(locale).format(template) : '';
};

const replaceSeparator = (format: string, language: string) => {
    const withSeparator = LanguageDateTimeFormatSeparatorOverride[language];
    if (!withSeparator) {
        return format;
    }

    const localDateSeparator = format.indexOf('/') > -1 ? '/' : format.indexOf('-') > -1 ? '-' : '.';
    return format.split(localDateSeparator).join(withSeparator);
};

export const getLocalMailItemRepresentationFromUTC = (value: string, language: string) => {
    const locale = LanguageLocaleMapper[language];
    const momentObject = moment.utc(value).locale(locale);
    let temptemplate = 'null';
    if (momentObject) {
        const today = moment();
        // if want to show todays date time for today's msg --
        if (today.isSame(momentObject, 'd')) {
            temptemplate = getDateTimeTemplate(DateTimeFormat.ONLY_TIME_TODAY, language, false);
        } else {
            // if want to show only date for msg --
            temptemplate = getDateTimeTemplate(DateTimeFormat.SHORT_FULL_DATE, language, false);
            // template = temptemplate;
        }
    }
    return value ? momentObject.local().format(temptemplate) : '';
};

export const getLocalDateTimeRepresentationFromUTC = (value: string, language: string) => {
    let template = '';
    const locale = LanguageLocaleMapper[language];
    const momentObject = moment.utc(value).locale(locale);
    if (momentObject) {
        template = getDateTimeTemplate(DateTimeFormat.ONLY_TIME, language, false);
    }
    return value ? momentObject.local().format(template) : '';
};

export const isDateGreaterThanToday = (dateToCheck: string, language: string): boolean => {
    const locale = LanguageLocaleMapper[language];
    const momentObject = moment.utc(dateToCheck).locale(locale);
    if (momentObject) {
        const today = moment();
        return today.isBefore(momentObject);
    }

    return false;
};
